import axios, { RawAxiosRequestHeaders } from "axios";
import General from "../../../../general";
import config from "../../../../config";
import {
  PartialBrandTheme,
  PartialQuestThemeData,
} from "../../tutorial/components/tutorial.types";
import Cookies from "universal-cookie";

export interface LeaderboardCampaignResponse {
  questThemeData?: PartialQuestThemeData;
  brandTheme?: {
    properties?: PartialBrandTheme;
  };
  showFooter?: boolean;
  uiProps?: any;
  campaignVariationId?: string;
}

export interface ApiCampaignResponse<T> {
  success: boolean;
  data: {
    sdkConfig?: {
      uiProps?: {
        questThemeData?: PartialQuestThemeData;
        BrandTheme?: {
          properties?: PartialBrandTheme;
        };
        showFooter?: boolean;
      };
    };
    campaignVariationId?: string;
  };
  error?: string;
}

export interface ApiHeaders {
  apiKey: string;
  userId?: string;
  token?: string;
  apiSecret?: string;
}

interface ExternalLoginParams {
  entityId: string;
  apiKey: string;
  uniqueUserId?: string;
  uniqueEmailId?: string;
  userId?: string;
  token?: string;
  entityAuthenticationToken?: string;
}

interface UserAuth {
  userId: string;
  token: string;
}

class LoginApi {
  private BACKEND_URL: string;
  private GeneralFunctions: General;
  private cookies: Cookies;

  constructor(apiType: string) {
    this.BACKEND_URL =
      apiType === "STAGING" ? config.BACKEND_URL_STAGING : config.BACKEND_URL;
    this.GeneralFunctions = new General("mixpanel", apiType);
    this.cookies = new Cookies();
  }

  private getAxiosHeaders(headers: ApiHeaders): RawAxiosRequestHeaders {
    return {
      "Content-Type": "application/json",
      ...headers,
    };
  }

  async sendOTP(
    email: string,
    entityId: string,
    headers: { apiKey: string; apiSecret: string }
  ) {
    try {
      const response = await axios.post(
        `${this.BACKEND_URL}api/users/email-login/send-otp?entityId=${entityId}`,
        { email, entityId },
        { headers }
      );
      return response.data;
    } catch (error) {
      this.GeneralFunctions.captureSentryException(error);
      throw error;
    }
  }

  async getUserAuth({
    apiKey,
    entityId,
    uniqueUserId,
    uniqueEmailId,
    userId,
    token,
    entityAuthenticationToken,
  }: ExternalLoginParams): Promise<UserAuth | null> {
    try {
      // Check cookies first
      const externalUserId = this.cookies.get("externalUserId");
      const questUserId = this.cookies.get("questUserId");
      const questUserToken = this.cookies.get("questUserToken");

      // If we have valid cookie data and it matches the uniqueUserId
      if (
        externalUserId &&
        questUserId &&
        questUserToken &&
        (externalUserId === uniqueUserId || externalUserId === uniqueEmailId)
      ) {
        return {
          userId: questUserId,
          token: questUserToken,
        };
      }

      // If uniqueUserId is provided, create new user regardless of userId/token
      if (uniqueUserId) {
        const headers = {
          apiKey,
          ...(token && { token }),
          ...(userId && { userId }),
          ...(!!entityAuthenticationToken && { "entity-authentication-token": entityAuthenticationToken }),
        };

        const body = {
          externalUserId: uniqueUserId,
          entityId,
          email: uniqueEmailId || null,
          ignoreMAU: true,
        };

        const response = await axios.post(
          `${this.BACKEND_URL}api/users/external/login`,
          body,
          { headers }
        );

        const { userId: newUserId, token: newToken } = response.data;
        const expires = new Date();
        expires.setHours(expires.getHours() + 12);

        this.cookies.set("externalUserId", uniqueUserId, {
          path: "/",
          expires,
        });
        this.cookies.set("questUserId", newUserId, { path: "/", expires });
        this.cookies.set("questUserToken", newToken, { path: "/", expires });

        return {
          userId: newUserId,
          token: newToken,
        };
      }

      // If no uniqueUserId but we have userId and token, use those
      if (userId && token) {
        return { userId, token };
      }

      return null;
    } catch (error) {
      this.GeneralFunctions.captureSentryException(error);
      return null;
    }
  }

  async verifyOTP(
    email: string,
    otp: string,
    entityId: string,
    headers: { apiKey: string; apiSecret: string },
    campaignId?: string,
    campaignVariationId?: string,
  ) {
    try {
      const response = await axios.post(
        `${this.BACKEND_URL}api/users/email-login/verify-otp`,
        { email, otp, entityId, campaignId, campaignVariationId },
        { headers }
      );
      return response.data;
    } catch (error) {
      this.GeneralFunctions.captureSentryException(error);
      throw error;
    }
  }

  async googleLogin(
    code: string,
    redirectUri: string,
    entityId: string,
    headers: { apiKey: string; apiSecret: string }
  ) {
    try {
      const response = await axios.post(
        `${this.BACKEND_URL}api/users/google/login`,
        {
          code,
          redirectUri,
          entityId,
        },
        { headers }
      );
      return response.data;
    } catch (error) {
      this.GeneralFunctions.captureSentryException(error);
      throw error;
    }
  }

  async getLoginCampaignResults(
    entityId: string,
    campaignId: string,
    headers: ApiHeaders,
    variation?: string,
  ): Promise<LeaderboardCampaignResponse> {
    try {
      const params = new URLSearchParams();
      params.set("platform", "REACT");
      if (variation) params.set("variation", variation);

      const request = `${
        this.BACKEND_URL
      }api/v2/entities/${entityId}/campaigns/${campaignId}?${params.toString()}`;

      const response = await axios.get<ApiCampaignResponse<any>>(request, {
        headers: this.getAxiosHeaders(headers),
      });

      if (response.data?.data?.sdkConfig) {
        return {
          questThemeData:
            response?.data?.data?.sdkConfig?.uiProps?.questThemeData,
          brandTheme: response?.data?.data?.sdkConfig?.uiProps?.BrandTheme,
          showFooter: response?.data?.data?.sdkConfig?.uiProps?.showFooter,
          uiProps: response?.data?.data?.sdkConfig?.uiProps,
          campaignVariationId: response?.data?.data?.campaignVariationId,
        };
      }

      return {
        questThemeData: undefined,
        brandTheme: undefined,
        showFooter: true,
        uiProps: undefined,
        campaignVariationId: undefined,
      };
    } catch (error) {
      this.GeneralFunctions.captureSentryException(error);
      return {
        questThemeData: undefined,
        brandTheme: undefined,
        showFooter: true,
        uiProps: undefined,
      };
    }
  }

  initiateGoogleLogin(googleClientId: string, redirectUri: string) {
    if (!googleClientId) {
      throw new Error(
        "This is preview of the saas app that we generated it for you. Google login will work once you add your Google Client ID in the code."
      );
    }

    return `https://accounts.google.com/o/oauth2/auth?client_id=${googleClientId}&redirect_uri=${redirectUri}&scope=profile%20email&response_type=code`;
  }

  trackEvent(eventName: string, category: string) {
    this.GeneralFunctions.fireTrackingEvent(eventName, category);
  }
}

export default LoginApi;
